import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/layout/DashboardLayout";
import PageHeader from "@/components/layout/PageHeader";
import BillingSummary from "@/components/billing/BillingSummary";
import PlanOptions from "@/components/billing/PlanOptions";
import IncludedEverywhere from "@/components/billing/IncludedEverywhere";
import ChangePlanDialog from "@/components/billing/ChangePlanDialog";
import CancelPlanDialog from "@/components/billing/CancelPlanDialog";
import TopUps from "@/components/billing/TopUps";
import Invoices from "@/components/billing/Invoices";
import { BillingInfo, planRank } from "@/components/billing/types";
import {
  CatalogPlan,
  ProxyTopupPackage,
  createCheckoutSession,
  createPortalSession,
  createProxyTopupSession,
  createTopupSession,
  fetchPlanCatalog,
  fetchProxyTopupPackages,
  keepCurrentPlan,
  reactivateSubscription,
} from "@/services/stripe";
import { getUserBillingInfo } from "@/services/user";
import { ErrorState, Skeleton as Block } from "@/components/ui/AsyncState";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import { useToast } from "@/context/ToastContext";
import { useUser } from "@/context/UserContext";
import { formatCount, formatDay, perThousand } from "@/utils/billingFormat";
import { Icon } from "@iconify/react";
import Head from "next/head";
import { useRouter } from "next/router";
import { captureBeforeRedirect } from "@/lib/posthog";

const apiMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const SectionTitle = ({
  title,
  sub,
  id,
}: {
  title: string;
  sub?: React.ReactNode;
  id?: string;
}) => (
  <div id={id} className="scroll-mt-6">
    <h2 className="text-base font-semibold text-fg">{title}</h2>
    {sub && <p className="mt-1 text-sm text-fg-muted">{sub}</p>}
  </div>
);

/** Shaped like the page: the three-column summary, then the plan cards. */
const Skeleton = () => (
  <div role="status" aria-live="polite" className="space-y-10">
    <span className="sr-only">Loading billing</span>
    <div className="grid grid-cols-1 divide-y divide-line rounded-card border border-line bg-surface md:grid-cols-3 md:divide-x md:divide-y-0">
      {[0, 1, 2].map(i => (
        <div key={i} className="space-y-3 p-6">
          <Block className="h-3.5 w-16" />
          <Block className="h-6 w-28" />
          <Block className="h-3.5 w-full" />
          <Block className="h-3.5 w-2/3" />
        </div>
      ))}
    </div>
    <div className="space-y-4">
      <Block className="h-5 w-32" />
      <Block className="h-[440px] rounded-card" />
    </div>
  </div>
);

/** One usage-based prompt, shown only when it's useful. */
function UsageNudge({
  info,
  catalog,
  onSeePlans,
  onSeeTopUps,
}: {
  info: BillingInfo;
  catalog: CatalogPlan[];
  onSeePlans: () => void;
  onSeeTopUps: () => void;
}) {
  const limit = info.currentPlan.creditLimit ?? 0;
  if (limit <= 0) return null;
  const used = info.usage.creditsUsed;
  const pct = (used / limit) * 100;
  if (pct < 80) return null;
  const plan = info.currentPlan.name;
  const out = used >= limit;
  const next = catalog.find(p => planRank(p.name) === planRank(plan) + 1);
  const renews = info.subscription?.renewsAt ? formatDay(info.subscription.renewsAt) : null;

  let text: string;
  if (plan === "Free") {
    text = out
      ? `You've used all ${formatCount(limit)} free credits, so new jobs are paused. Choose a plan to keep going.`
      : `You've used ${formatCount(used)} of your ${formatCount(limit)} free credits.${next ? ` ${next.name} gives you ${formatCount(next.includedCredits)} credits every month.` : ""}`;
  } else {
    text = out
      ? `You've used all your credits${renews ? ` until ${renews}` : ""}, so new jobs are paused. Add a top-up to keep going now${next ? `, or move to ${next.name} for more every month` : ""}.`
      : `You've used ${Math.floor(pct)}% of your credits${renews ? ` and they reset on ${renews}` : ""}. A top-up adds more right away${next ? `, or ${next.name} gives you ${formatCount(next.includedCredits)} a month at ${perThousand(next.priceCents, next.includedCredits ?? 0)} per 1,000` : ""}.`;
  }

  return (
    <div
      role={out ? "alert" : "status"}
      className={`rounded-card border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
        out ? "border-danger/30 bg-danger-subtle" : "border-warning/30 bg-warning-subtle"
      }`}
    >
      <div className="flex items-start gap-3 flex-1">
        <Icon
          icon={out ? "mdi:alert-circle" : "mdi:gauge"}
          className={`text-xl shrink-0 ${out ? "text-danger-text" : "text-warning-text"}`}
          aria-hidden="true"
        />
        <p className={`text-sm ${out ? "text-danger-text" : "text-warning-text"}`}>{text}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        {plan !== "Free" && (
          <Button variant="secondary" onClick={onSeeTopUps}>
            Add a top-up
          </Button>
        )}
        {next && (
          <Button onClick={onSeePlans}>{plan === "Free" ? "Choose a plan" : "See plans"}</Button>
        )}
      </div>
    </div>
  );
}

const Billing = () => {
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [catalog, setCatalog] = useState<CatalogPlan[]>([]);
  const [proxyPackages, setProxyPackages] = useState<ProxyTopupPackage[]>([]);
  const [proxyState, setProxyState] = useState<"loading" | "error" | "ready">("loading");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changeTarget, setChangeTarget] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const [busy, setBusy] = useState<"keep" | "reactivate" | "portal" | null>(null);
  const { addToast } = useToast();
  const { refetchUser } = useUser();
  const router = useRouter();
  const plansRef = useRef<HTMLDivElement>(null);
  const topupsRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const [billing, plans] = await Promise.all([
      getUserBillingInfo(),
      fetchPlanCatalog().catch(() => null),
    ]);
    if (billing.success && plans) {
      setInfo(billing.data);
      setCatalog(plans);
      setError(null);
    } else {
      setError(billing.success ? "Couldn't load plans" : billing.error || "Couldn't load billing");
    }
  }, []);

  const loadProxy = useCallback(() => {
    setProxyState("loading");
    fetchProxyTopupPackages()
      .then(p => {
        setProxyPackages(Array.isArray(p) ? p : []);
        setProxyState("ready");
      })
      .catch(() => setProxyState("error"));
  }, []);

  const retryLoad = useCallback(() => {
    setLoading(true);
    load()
      .catch(() =>
        setError("Your billing details couldn't be loaded. Check your connection and try again.")
      )
      .finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    retryLoad();
    loadProxy();
  }, [retryLoad, loadProxy]);

  // Coming back from Stripe Checkout.
  useEffect(() => {
    const q = router.query;
    const message =
      q.success === "1"
        ? "Your plan is active. Thanks for subscribing."
        : q.topup_success === "1"
          ? "Credits added."
          : q.proxy_topup_success === "1"
            ? "Proxy bandwidth added."
            : null;
    if (message) {
      addToast({ message, type: "success" });
      router.replace("/dashboard/billing", undefined, { shallow: true });
      load();
      refetchUser();
    } else if (q.canceled === "1") {
      router.replace("/dashboard/billing", undefined, { shallow: true });
    }
  }, [router.query, addToast, router, load, refetchUser]);

  const refresh = async () => {
    await load();
    refetchUser();
  };

  const openPortal = async () => {
    if (busy === "portal") return;
    setBusy("portal");
    try {
      const { url } = await createPortalSession();
      // Stay busy while the browser leaves for Stripe, so a second click can't open a second session.
      if (url) return captureBeforeRedirect("billing_portal_opened", {}, url);
      setBusy(null);
    } catch (err) {
      addToast({
        message: apiMessage(err, "Couldn't open the billing portal. Try again."),
        type: "error",
      });
      setBusy(null);
    }
  };

  const choosePlan = async (planName: string) => {
    if (!info) return;
    // Free accounts subscribe through Stripe Checkout. Paying accounts change plan in place.
    if (info.currentPlan.name === "Free") {
      setBusyPlan(planName);
      try {
        const { url } = await createCheckoutSession(planName);
        if (url)
          return captureBeforeRedirect(
            "checkout_redirect_clicked",
            { type: "subscription", plan_name: planName },
            url
          );
        setBusyPlan(null);
      } catch (err) {
        addToast({
          message: apiMessage(err, "Couldn't start checkout. Try again."),
          type: "error",
        });
        setBusyPlan(null);
      }
      return;
    }
    setShowCancel(false);
    setChangeTarget(planName);
  };

  const buyCredits = async (credits: number) => {
    if (buying) return;
    setBuying(`credits-${credits}`);
    try {
      const { url } = await createTopupSession(credits);
      if (url)
        return captureBeforeRedirect(
          "checkout_redirect_clicked",
          { type: "topup", credits_amount: credits },
          url
        );
      setBuying(null);
    } catch (err) {
      addToast({
        message: apiMessage(err, "Couldn't start the top-up. Try again."),
        type: "error",
      });
      setBuying(null);
    }
  };

  const buyProxy = async (bandwidthMb: number) => {
    if (buying) return;
    setBuying(`proxy-${bandwidthMb}`);
    try {
      const { url } = await createProxyTopupSession(bandwidthMb);
      if (url)
        return captureBeforeRedirect(
          "checkout_redirect_clicked",
          { type: "proxy_topup", bandwidth_mb: bandwidthMb },
          url
        );
      setBuying(null);
    } catch (err) {
      addToast({
        message: apiMessage(err, "Couldn't start the top-up. Try again."),
        type: "error",
      });
      setBuying(null);
    }
  };

  const keepPlan = async () => {
    setBusy("keep");
    try {
      await keepCurrentPlan();
      addToast({ message: `You're staying on ${info?.currentPlan.name}.`, type: "success" });
      await refresh();
    } catch (err) {
      addToast({
        message: apiMessage(err, "Couldn't keep your plan. Please try again."),
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  const reactivate = async () => {
    setBusy("reactivate");
    try {
      await reactivateSubscription();
      addToast({
        message: `Your ${info?.currentPlan.name} plan will keep renewing.`,
        type: "success",
      });
      await refresh();
    } catch (err) {
      addToast({
        message: apiMessage(err, "Couldn't keep your plan. Please try again."),
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const plan = info?.currentPlan.name ?? "Free";
  const selfServe = planRank(plan) >= 0;
  const sub = info?.subscription ?? null;
  const ending = !!sub && (sub.cancelAtPeriodEnd || sub.status === "canceling");
  const isPaid = plan !== "Free" && selfServe;
  const cheaperPlan =
    catalog.find(p => planRank(p.name) === planRank(plan) - 1 && p.name !== "Free") ?? null;

  return (
    <>
      <Head>
        <title>Billing | Spidra</title>
      </Head>
      <PageHeader title="Billing" description="Your plan, credits, top-ups and invoices." />

      <div className="space-y-10 pb-10">
        {loading ? (
          <Skeleton />
        ) : error || !info ? (
          <Card padding="none">
            <ErrorState
              title="Couldn't load billing"
              error={error || "Your billing details couldn't be loaded."}
              onRetry={retryLoad}
              retrying={loading}
            />
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              <BillingSummary
                info={info}
                catalog={catalog}
                onKeepPlan={keepPlan}
                onReactivate={reactivate}
                onOpenPortal={openPortal}
                busy={busy}
              />
              <UsageNudge
                info={info}
                catalog={catalog}
                onSeePlans={() => scrollTo(plansRef)}
                onSeeTopUps={() => scrollTo(topupsRef)}
              />
            </div>

            {selfServe ? (
              <section className="space-y-4" ref={plansRef}>
                <SectionTitle
                  title={plan === "Free" ? "Choose a plan" : "Plans"}
                  sub={
                    <>
                      Every plan has every feature. You pay for volume.{" "}
                      <a
                        href="https://docs.spidra.io/billing/how-credits-work"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-text hover:underline"
                      >
                        How credits work
                      </a>
                    </>
                  }
                />
                <PlanOptions
                  catalog={catalog}
                  currentPlan={plan}
                  pendingPlan={sub?.pendingPlan ?? null}
                  ending={ending}
                  busyPlan={busyPlan}
                  onChoose={choosePlan}
                />
                <p className="flex items-start gap-2 text-xs text-fg-muted">
                  <Icon
                    icon="mdi:lock-outline"
                    className="text-sm shrink-0 mt-px"
                    aria-hidden="true"
                  />
                  <span>
                    Payments are handled by Stripe. Upgrades start right away: you pay a month of the new
                    plan, less what's left of your current one, and your billing date moves to today.
                    Downgrades and cancellations take effect at your next renewal. Cancel any time.
                  </span>
                </p>
                <IncludedEverywhere />
              </section>
            ) : (
              <section className="bg-surface border border-line rounded-card shadow-card p-5 text-sm text-fg-secondary">
                You&apos;re on a custom plan. To change it, email{" "}
                <a href="mailto:hello@spidra.io" className="text-primary-text hover:underline">
                  hello@spidra.io
                </a>
                .
              </section>
            )}

            <section className="space-y-4" ref={topupsRef}>
              <SectionTitle
                title="Top-ups"
                sub="One-time purchases on top of your plan. Unused top-ups last 12 months."
              />
              <TopUps
                currentPlan={plan}
                catalog={catalog}
                proxyPackages={proxyPackages}
                proxyState={proxyState}
                onRetryProxy={loadProxy}
                buying={buying}
                onBuyCredits={buyCredits}
                onBuyProxy={buyProxy}
              />
            </section>

            {(isPaid || info.invoices.length > 0) && (
              <section className="space-y-4">
                <SectionTitle title="Invoices" />
                <Invoices
                  invoices={info.invoices}
                  onOpenPortal={openPortal}
                  opening={busy === "portal"}
                />
              </section>
            )}

            <section className="bg-deep relative flex flex-col gap-4 overflow-hidden rounded-card p-6 text-white shadow-card sm:flex-row sm:items-center sm:px-8">
              <span aria-hidden="true" className="dot-field" />
              <div className="relative flex-1">
                <p className="text-15 font-medium">
                  Need more than {selfServe ? "Pro" : "your plan"}?
                </p>
                <p className="mt-1 max-w-xl text-sm text-white/75">
                  For millions of credits a month: higher concurrency and rate limits, private proxy
                  pools, volume pricing and a dedicated contact.
                </p>
              </div>
              <a
                href="mailto:hello@spidra.io?subject=Spidra%20Enterprise"
                className="relative inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-control bg-white px-4 text-sm font-medium text-primary transition-colors hover:bg-white/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Talk to us
                <Icon icon="mdi:arrow-right" className="text-base" aria-hidden="true" />
              </a>
            </section>

            {isPaid && !ending && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCancel(true)}
                  className="text-fg-muted hover:!text-danger-text"
                >
                  Cancel plan
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {info && (
        <>
          <ChangePlanDialog
            target={changeTarget}
            currentPlan={plan}
            catalog={catalog}
            card={info.paymentMethod}
            onClose={() => setChangeTarget(null)}
            onOpenPortal={openPortal}
            onDone={async result => {
              setChangeTarget(null);
              addToast({
                message:
                  result.status === "done"
                    ? `You're on ${result.plan} now. Your new credits are ready.`
                    : `You'll move to ${result.plan} on ${formatDay(result.effectiveAt)}.`,
                type: "success",
              });
              await refresh();
            }}
          />
          <CancelPlanDialog
            isOpen={showCancel}
            currentPlan={plan}
            endsAt={sub?.renewsAt ?? null}
            hasTopUps={info.usage.additionalCredits > 0 || info.usage.additionalProxyMb > 0}
            cheaperPlan={cheaperPlan}
            onClose={() => setShowCancel(false)}
            onSwitchInstead={choosePlan}
            onCancelled={async () => {
              setShowCancel(false);
              addToast({
                message: `Your plan ends on ${formatDay(sub?.renewsAt)}. You can keep it any time before then.`,
                type: "success",
              });
              await refresh();
            }}
          />
        </>
      )}
    </>
  );
};

Billing.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Billing;
